import type { CurveColor, IfcCurvePrimitive } from "../../../../domain/utilities/CurvePrimitives";

function ifcReal(n: number, decimals = 12): string {
  if (!Number.isFinite(n)) return "0.";
  let s = n.toFixed(decimals).replace(/0+$/, "");
  if (s.endsWith(".")) return s;
  if (!s.includes(".")) return `${s}.`;
  return s;
}

function addPoint3d(
  getNextId: () => number,
  emit: (line: string) => void,
  p: [number, number, number]
): number {
  const id = getNextId();
  emit(`#${id}=IFCCARTESIANPOINT((${ifcReal(p[0])},${ifcReal(p[1])},${ifcReal(p[2])}));`);
  return id;
}

function vectorLength(v: [number, number, number]): number {
  return Math.hypot(v[0], v[1], v[2]);
}

function pointsCoincident(
  a: [number, number, number],
  b: [number, number, number],
  tolerance = 1e-6
): boolean {
  return (
    Math.abs(a[0] - b[0]) <= tolerance &&
    Math.abs(a[1] - b[1]) <= tolerance &&
    Math.abs(a[2] - b[2]) <= tolerance
  );
}

function normalize(v: [number, number, number]): [number, number, number] {
  const len = vectorLength(v);
  if (!Number.isFinite(len) || len < 1e-12) return [1, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

function cross(a: [number, number, number], b: [number, number, number]): [number, number, number] {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function colorKey(c: CurveColor): string {
  const a = c.a !== undefined ? c.a : -1;
  return `${c.r.toFixed(6)}|${c.g.toFixed(6)}|${c.b.toFixed(6)}|${a.toFixed(6)}`;
}

export function createCurveRepresentationFromPrimitives(args: {
  getNextId: () => number;
  geometricContextId: number;
  curves: IfcCurvePrimitive[];
  wrapInProductShape?: boolean;
}): { output: string; shapeRepId: number; productShapeId?: number } {
  const { getNextId, geometricContextId, curves } = args;
  const wrapInProductShape = args.wrapInProductShape ?? true;
  let output = "";
  const emit = (line: string) => {
    output += `${line}\n`;
  };

  const itemIds: number[] = [];
  const styleCache = new Map<string, number>();

  const addSurfaceStyleForItem = (itemId: number, color: CurveColor): void => {
    const key = colorKey(color);
    let surfaceStyleId = styleCache.get(key);

    if (!surfaceStyleId) {
      const colourId = getNextId();
      emit(`#${colourId}=IFCCOLOURRGB($,${ifcReal(color.r, 6)},${ifcReal(color.g, 6)},${ifcReal(color.b, 6)});`);

      const transparency = color.a !== undefined ? 1 - Math.min(Math.max(color.a, 0), 1) : undefined;
      const shadingId = getNextId();
      emit(
        `#${shadingId}=IFCSURFACESTYLESHADING(#${colourId},${transparency !== undefined ? ifcReal(transparency, 6) : "$"});`
      );

      surfaceStyleId = getNextId();
      emit(`#${surfaceStyleId}=IFCSURFACESTYLE($,.BOTH.,(#${shadingId}));`);
      styleCache.set(key, surfaceStyleId);
    }

    const styleItemId = getNextId();
    emit(`#${styleItemId}=IFCSTYLEDITEM(#${itemId},(#${surfaceStyleId}),$);`);
  };

  const addGeometricSetItem = (geometricItemId: number, color?: CurveColor): void => {
    const setId = getNextId();
    emit(`#${setId}=IFCGEOMETRICSET((#${geometricItemId}));`);
    if (color) addSurfaceStyleForItem(setId, color);
    itemIds.push(setId);
  };

  for (const curve of curves) {
    if (curve.kind === "line") {
      if (pointsCoincident(curve.start, curve.end)) continue;

      const p0 = addPoint3d(getNextId, emit, curve.start);
      const p1 = addPoint3d(getNextId, emit, curve.end);
      const lineId = getNextId();
      emit(`#${lineId}=IFCPOLYLINE((#${p0},#${p1}));`);
      addGeometricSetItem(lineId, curve.color);
      continue;
    }

    if (curve.kind === "polyline") {
      if (curve.points.length < 2) continue;
      const filteredPoints: [number, number, number][] = [];
      for (const p of curve.points) {
        if (filteredPoints.length === 0 || !pointsCoincident(filteredPoints[filteredPoints.length - 1], p)) {
          filteredPoints.push(p);
        }
      }
      if (filteredPoints.length < 2) continue;

      const pointIds = filteredPoints.map((p) => addPoint3d(getNextId, emit, p));
      const polylineId = getNextId();
      emit(`#${polylineId}=IFCPOLYLINE((#${pointIds.join(",#")}));`);
      addGeometricSetItem(polylineId, curve.color);
      continue;
    }

    if (curve.kind === "arc") {
      if (!curve.isCircle) {
        if (pointsCoincident(curve.start, curve.end)) continue;

        const p0 = addPoint3d(getNextId, emit, curve.start);
        const p1 = addPoint3d(getNextId, emit, curve.end);
        const lineId = getNextId();
        emit(`#${lineId}=IFCPOLYLINE((#${p0},#${p1}));`);
        addGeometricSetItem(lineId, curve.color);
        continue;
      }

      const centerId = addPoint3d(getNextId, emit, curve.center);

      const xDir = normalize(curve.vector0);
      const zDirRaw = cross(curve.vector0, curve.vector90);
      const zDir = normalize(zDirRaw[0] === 0 && zDirRaw[1] === 0 && zDirRaw[2] === 0 ? [0, 0, 1] : zDirRaw);

      const zDirId = getNextId();
      emit(`#${zDirId}=IFCDIRECTION((${ifcReal(zDir[0])},${ifcReal(zDir[1])},${ifcReal(zDir[2])}));`);
      const xDirId = getNextId();
      emit(`#${xDirId}=IFCDIRECTION((${ifcReal(xDir[0])},${ifcReal(xDir[1])},${ifcReal(xDir[2])}));`);

      const axisId = getNextId();
      emit(`#${axisId}=IFCAXIS2PLACEMENT3D(#${centerId},#${zDirId},#${xDirId});`);

      const radius = vectorLength(curve.vector0);
      const circleId = getNextId();
      emit(`#${circleId}=IFCCIRCLE(#${axisId},${ifcReal(radius)});`);

      if (curve.isFullCircle) {
        addGeometricSetItem(circleId, curve.color);
      } else {
        const startId = addPoint3d(getNextId, emit, curve.start);
        const endId = addPoint3d(getNextId, emit, curve.end);
        const trimmedId = getNextId();
        emit(
          `#${trimmedId}=IFCTRIMMEDCURVE(#${circleId},(#${startId}),(#${endId}),.T.,.CARTESIAN.);`
        );
        addGeometricSetItem(trimmedId, curve.color);
      }
    }
  }

  if (itemIds.length === 0) {
    const shapeRepId = getNextId();
    emit(`#${shapeRepId}=IFCSHAPEREPRESENTATION(#${geometricContextId},'Body','GeometricSet',());`);
    if (!wrapInProductShape) {
      return { output, shapeRepId };
    }
    const productShapeId = getNextId();
    emit(`#${productShapeId}=IFCPRODUCTDEFINITIONSHAPE($,$,(#${shapeRepId}));`);
    return { output, shapeRepId, productShapeId };
  }

  const shapeRepId = getNextId();
  emit(
    `#${shapeRepId}=IFCSHAPEREPRESENTATION(#${geometricContextId},'Body','GeometricSet',(#${itemIds.join(",#")}));`
  );
  if (!wrapInProductShape) {
    return { output, shapeRepId };
  }
  const productShapeId = getNextId();
  emit(`#${productShapeId}=IFCPRODUCTDEFINITIONSHAPE($,$,(#${shapeRepId}));`);

  return { output, shapeRepId, productShapeId };
}
